import { useEffect, useState } from 'react'

import { useClearUserMetadataFromHash, useFirebase } from '@scouts/auth'
import { getExpoPushToken } from '@taxfix/scouts-helpers'
import { colors, Loadable } from '@taxfix/scouts-ui'

import { initSentryUser } from '@/helpers/sentry'
import { useAppDispatch } from '@/store'
import { enablePushNotifications, loggedin } from '@/store/profile'
import { useEventTracking, TrackingEvent } from '@/telemetry'
import { track, TRACK_USER_SIGNUP } from '@/trackers'

import { useAuthenticationProfileData } from './hooks/use-authentication-profile-data'
import { useResetSessionAndLogout } from './hooks/use-reset-session-and-logout'

import { AuthenticationContainer } from './AuthenticationContainer'

const useAuthentication = () => {
    const dispatch = useAppDispatch()

    const { capture, identify } = useEventTracking()

    const { user, isInitialized } = useFirebase()
    const { resetSessionAndLogout } = useResetSessionAndLogout()

    const [shouldAttemptAuthentication, setShouldAttemptAuthentication] = useState(true)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const { profileData } = useAuthenticationProfileData()
    const { clearUserMetadataFromHash } = useClearUserMetadataFromHash()

    useEffect(() => {
        // Do nothing if Firebase is not initialized

        if (!isInitialized) return

        // Reset state if the user is not logged in with Firebase

        if (!user) {
            setIsAuthenticated(false)
            setShouldAttemptAuthentication(true)
            return
        }

        // Skip authentication against API if we've already tried

        if (!shouldAttemptAuthentication) return

        // Attempt authentication against API

        setIsLoading(true)
        setShouldAttemptAuthentication(false)

        dispatch(loggedin(profileData))
            .then((result) => {
                if ('error' in result) {
                    setIsAuthenticated(false)
                    resetSessionAndLogout()
                    return
                }

                clearUserMetadataFromHash()

                const profile = result.payload.profile
                setIsAuthenticated(true)

                if (profile?.id) {
                    initSentryUser(profile)

                    identify(profile.id, {
                        initial_variant: profile.initialVariant || null,
                        referral_source: profile.referralSource || null,
                        ...(result.payload.isSignUp
                            ? {
                                  cookie_utm_campaign: result.payload.extraCookieData.marketingData.utmCampaign || null,
                                  cookie_utm_content: result.payload.extraCookieData.marketingData.utmContent || null,
                                  cookie_utm_medium: result.payload.extraCookieData.marketingData.utmMedium || null,
                                  cookie_utm_source: result.payload.extraCookieData.marketingData.utmSource || null,
                                  cookie_utm_term: result.payload.extraCookieData.marketingData.utmTerm || null,
                              }
                            : {}),
                    })
                }

                if (result.payload.isSignUp) {
                    track(TRACK_USER_SIGNUP, {
                        email: profile.email,
                        hashedEmail: result.payload.hashedEmail,
                        initialVariant: profile.initialVariant,
                        referralSource: profile.referralSource,
                        userId: profile.id,
                        variant: profile.variant,
                    })

                    capture(TrackingEvent.UserSignedUp, {
                        hashed_email: result.payload.hashedEmail,
                        referral_source: profile.referralSource,
                        variant: profile.variant,
                    })
                }

                const expoPushToken = getExpoPushToken()

                if (expoPushToken?.includes('ExponentPushToken[')) {
                    dispatch(enablePushNotifications(expoPushToken))
                }
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [
        capture,
        clearUserMetadataFromHash,
        dispatch,
        identify,
        isInitialized,
        profileData,
        resetSessionAndLogout,
        shouldAttemptAuthentication,
        user,
    ])

    return { isAuthenticated, isLoading, isInitialized, user }
}

export const AuthenticationBoundary = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated, isLoading, isInitialized, user } = useAuthentication()

    // #1 Render loading state while Firebase is initializing

    if (!isInitialized) {
        // Spinners in this component have different colours to observe the loading state of the app – we can remove this down the line
        return <Loadable isLoading isFullViewportHeight />
    }

    // #2 Render login/signup/reset password pages if not authenticated with Firebase

    if (!user) return <AuthenticationContainer />

    // #3 Render loading state while we check if the user is authenticated against our API

    if (isLoading) {
        return <Loadable isLoading isFullViewportHeight />
    }

    // #4 Render children if the user is authenticated against our API

    if (isAuthenticated) {
        return <>{children}</>
    }

    // #5 Render loading state for any other case as a fallback

    return <Loadable spinnerColor={colors.surface.alert} isLoading isFullViewportHeight />
}
